// Het beheerscherm: de schil met acht vlakken (W6, owner-besluit V1).
//
// Tot W6 was dit één pagina met de wachtrij en de gebruikerslijst onder elkaar
// (W9). Die twee zijn ongewijzigd verhuisd naar `beheer/Wachtrij.tsx` en
// `beheer/Gebruikers.tsx`; deze schil draagt alleen de paginakop, de
// subnavigatie en het actieve vlak. Tot en met ronde 5 toonden de vlakken die
// nog niet gebouwd waren eerlijk in welke ronde ze kwamen; sinds ronde 6 is elk
// vlak gebouwd en is die tussenstand weg.
//
// Twee dingen die deze schil bewust wél zelf doet:
//
//  1. De wachtrij en de gebruikerslijst ophalen. De paginakop toont van allebei
//     het aantal; zou elk vlak ze zelf ophalen, dan vraagt het scherm hetzelfde
//     getal twee keer en kunnen kop en vlak uit elkaar lopen.
//  2. Het gekozen vlak melden aan het gebruikslog (W6, research §4) — alleen
//     WELK vlak, nooit wat erin staat — en het onthouden in de browser, zodat
//     een beheerder terugkomt waar hij was.
//
// Sinds W6 ronde 2 is Overzicht het standaardvlak: wie binnenkomt ziet eerst
// of er iets op hem wacht en of de machine draait. Een onthouden keuze gaat
// voor; de standaard geldt alleen als er niets (geldigs) bewaard is.
//
// Sinds W6 ronde 3 staat ook Bronnen & dekking (`beheer/Bronnen.tsx`). Dat
// vlak meldt zijn eigen tabbladen aan het gebruikslog ("Beheer/Bronnen/…");
// deze schil meldt zoals altijd alleen het vlak.
//
// Sinds W6 ronde 4 staat ook Kosten & credits (`beheer/Kosten.tsx`). Dat vlak
// haalt zijn eigen cijfers op; het gebruikslog krijgt "Beheer/Kosten" via de
// gewone vlakmelding hieronder.
//
// Sinds W6 ronde 5 staat ook Kwaliteit (`beheer/Kwaliteit.tsx`): de meters en
// de chatlogs. Net als Bronnen meldt het zijn eigen tabbladen aan het
// gebruikslog ("Beheer/Kwaliteit/Chatlogs").
//
// Sinds W6 ronde 6 staat ook Configuratie (`beheer/Configuratie.tsx`):
// onderwerpen, zoekopdrachten en instellingen (research §3.7, V5/V6). Ook dat
// vlak meldt zijn eigen tabbladen ("Beheer/Configuratie/Instellingen").
//
// Sinds W6 ronde 7 heeft Gebruikers & engagement twee tabbladen: Mensen (de
// lijst van W9) en Gebruik (tellingen, tijden en krediet per persoon, research
// §3.8). Het vlak meldt ze zelf ("Beheer/Gebruikers/Gebruik"); de lijst haalt
// deze schil nog steeds op, want de paginakop telt hem.
//
// Sinds Spoor B (sessie B2) staat er een negende vlak: Meterkast
// (`beheer/Meterkast.tsx`) — het controlescherm uit besluit D2, op de tabel en
// het endpoint van sessie B1. Het staat achteraan omdat het over de MACHINE
// gaat en niet over het werk van vandaag; het meldt zijn eigen borden
// ("Beheer/Meterkast/Bord3").
//
// Sinds K2 (07-10) staat er "Funding nakijken" (`beheer/FundingNakijken.tsx`)
// direct na de Wachtrij: ook dat is werk dat op een besluit wacht. De knop
// draagt het aantal open controles ("Funding nakijken · 3 open"); de schil haalt
// dat getal één keer op, het vlak houdt het daarna bij na elk besluit.

import { useEffect, useState } from "react";

import { leesParam, useAdresParam, zetParam } from "../lib/adres";

import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { useResource, type Resource } from "../lib/useResource";
import type { GebruikersResponse, WachtrijResponse } from "../contracts";
import { color, font, radius } from "../tokens";
import { Bronnen } from "./beheer/Bronnen";
import { Configuratie } from "./beheer/Configuratie";
import { FundingNakijken } from "./beheer/FundingNakijken";
import { Gebruikers } from "./beheer/Gebruikers";
import { Gezondheid } from "./beheer/Gezondheid";
import { Ketenbalans } from "./beheer/Ketenbalans";
import { Kosten } from "./beheer/Kosten";
import { Kwaliteit } from "./beheer/Kwaliteit";
import { Meterkast } from "./beheer/Meterkast";
import { Overzicht } from "./beheer/Overzicht";
import { Plannen } from "./beheer/Plannen";
import { Validatie } from "./beheer/Validatie";
import { Wachtrij } from "./beheer/Wachtrij";

/** De korte ascii-sleutel reist mee naar het gebruikslog ("Beheer/Kosten");
 *  het label is wat de beheerder leest. */
type VlakKey =
  | "Overzicht"
  | "Wachtrij"
  | "Funding"
  | "Gezondheid"
  | "Bronnen"
  | "Configuratie"
  | "Kwaliteit"
  | "Kosten"
  | "Gebruikers"
  | "Meterkast"
  | "Ketenbalans"
  | "Validatie"
  | "Plannen";

/** Volgorde uit besluit V1. */
const VLAKKEN: readonly { key: VlakKey; label: string }[] = [
  { key: "Overzicht", label: "Overzicht" },
  { key: "Wachtrij", label: "Wachtrij" },
  // K2 (07-10): wat de funding-load niet zelf kon beslissen.
  { key: "Funding", label: "Funding nakijken" },
  { key: "Gezondheid", label: "Gezondheid" },
  { key: "Bronnen", label: "Bronnen & dekking" },
  { key: "Configuratie", label: "Configuratie" },
  { key: "Kwaliteit", label: "Kwaliteit" },
  { key: "Kosten", label: "Kosten & credits" },
  { key: "Gebruikers", label: "Gebruikers & engagement" },
  { key: "Meterkast", label: "Meterkast" },
  // Sessie 0b (27-10): verdwijnt er per stap iets stil uit de keten, en de poort.
  { key: "Ketenbalans", label: "Ketenbalans" },
  // VT-1: wat de accountmanager zei over het validatiedocument, per regel.
  { key: "Validatie", label: "Validatie" },
  // Pilot 26-10 (baan 2P): per propositie het plan van AM en BP naast elkaar.
  { key: "Plannen", label: "Plannen" },
];

const STANDAARD_VLAK: VlakKey = "Overzicht";
const VLAK_SLEUTEL = "sof.beheer.vlak";

/** Een kaart op het Overzicht zegt in kleine letters waar hij heen wijst
 *  (`kaart.vlak`: wachtrij, gezondheid, bronnen, …) — dezelfde woorden als
 *  onze sleutels, alleen zonder hoofdletter. Onbekend = niet wisselen: een
 *  klik die nergens heen gaat is beter dan een klik naar het verkeerde vlak. */
function vlakUitKaart(vlak: string): VlakKey | null {
  return VLAKKEN.find((v) => v.key.toLowerCase() === vlak.toLowerCase())?.key ?? null;
}

/** Het onthouden vlak — of de standaard als er niets (geldigs) staat. Local
 *  storage kan ontbreken of weigeren; dan opent Beheer gewoon op de standaard. */
function leesVlak(): VlakKey {
  try {
    const bewaard = window.localStorage.getItem(VLAK_SLEUTEL);
    const vlak = VLAKKEN.find((v) => v.key === bewaard);
    if (vlak) return vlak.key;
  } catch {
    // geen opslag: dan de standaard
  }
  return STANDAARD_VLAK;
}

/**
 * Het getal in de paginakop. Review ronde 2: nooit een verzonnen nul. Zolang
 * een lijst nog komt staat er "laden…"; mislukte het ophalen, dan zegt de kop
 * dát — anders leest "0 gebruikers" of "niets op de wachtrij" als een meting.
 * Tijdens een herlaadronde blijft het vorige getal staan (`data` blijft).
 */
function kopTelling(
  gebruikers: Resource<GebruikersResponse>,
  wachtrij: Resource<WachtrijResponse>,
): string {
  const n = gebruikers.data?.gebruikers.length;
  const wie = gebruikers.error
    ? "gebruikers niet opgehaald"
    : n === undefined
      ? null
      : `${n} ${n === 1 ? "gebruiker" : "gebruikers"}`;

  const open = wachtrij.data?.openTotaal;
  const wat = wachtrij.error
    ? "wachtrij niet opgehaald"
    : open === undefined
      ? null
      : open > 0
        ? `${open} op de wachtrij`
        : "niets op de wachtrij";

  if (wie === null && wat === null) return "laden…";
  return `${wie ?? "laden…"} · ${wat ?? "laden…"}`;
}

export function BeheerPagina({ mijnEmail }: { mijnEmail?: string }) {
  const gebruikers = useResource<GebruikersResponse>(() => api.gebruikers(), []);
  const wachtrij = useResource(() => api.wachtrij(), []);
  // Het vlak staat in het adres (?vlak=Kwaliteit): terug gaat naar het vorige vlak, herladen
  // blijft op het vlak. Zonder (geldig) adres: het onthouden vlak uit localStorage.
  const [vlakParam] = useAdresParam("vlak");
  const vlak: VlakKey = VLAKKEN.find((v) => v.key === vlakParam)?.key ?? leesVlak();
  useEffect(() => {
    // De eerste regel in de geschiedenis krijgt het vlak mee, anders zou terug naar het
    // laatst onthouden vlak springen in plaats van naar waar je begon.
    if (!VLAKKEN.some((v) => v.key === leesParam("vlak"))) zetParam("vlak", leesVlak(), "vervang");
  }, []);
  // Het getal op de knop "Funding nakijken". Eén keer opgehaald; het vlak
  // werkt het bij na elk besluit. Mislukt het, dan staat er geen getal (nooit
  // een verzonnen nul).
  const fundingLijst = useResource(() => api.fundingControles({ besluit: "open" }), []);
  const [fundingOpen, setFundingOpen] = useState<number | null>(null);
  const fundingN = fundingOpen ?? fundingLijst.data?.tellingen.open ?? null;

  // Ook het eerste vlak bij het openen telt: anders ziet het log alleen wie
  // wisselt, en niet waar een beheerder de meeste tijd doorbrengt.
  // Vuur-en-vergeet: een telling die faalt mag het scherm nooit raken.
  useEffect(() => {
    void api.gebruik({ scherm: `Beheer/${vlak}` }).catch(() => {});
  }, [vlak]);

  const kies = (nieuw: VlakKey) => {
    zetParam("vlak", nieuw, "push");
    try {
      window.localStorage.setItem(VLAK_SLEUTEL, nieuw);
    } catch {
      // niet te onthouden; het vlak wisselt wel
    }
  };

  return (
    <div style={{ fontFamily: font }}>
      <PageHeader title="Beheer" count={kopTelling(gebruikers, wachtrij)} />

      {/* Zelfde gesegmenteerde knoppen als de weergavekeuze op Skills
          (`view-btn`): één patroon voor "kies wat je ziet". */}
      <nav
        aria-label="Beheervlakken"
        style={{
          display: "inline-flex",
          flexWrap: "wrap",
          gap: 4,
          background: color.lineFaint,
          borderRadius: radius.input,
          padding: 3,
          marginBottom: 20,
        }}
      >
        {VLAKKEN.map((v) => (
          <button
            key={v.key}
            className={"view-btn" + (vlak === v.key ? " active" : "")}
            aria-pressed={vlak === v.key}
            onClick={() => kies(v.key)}
          >
            {v.label}
            {v.key === "Funding" && fundingN !== null ? ` · ${fundingN} open` : ""}
          </button>
        ))}
      </nav>

      {vlak === "Overzicht" ? (
        <Overzicht
          onKiesVlak={(doel) => {
            const nieuw = vlakUitKaart(doel);
            if (nieuw) kies(nieuw);
          }}
        />
      ) : vlak === "Wachtrij" ? (
        <Wachtrij wachtrij={wachtrij} />
      ) : vlak === "Funding" ? (
        <FundingNakijken onTelling={setFundingOpen} />
      ) : vlak === "Gezondheid" ? (
        <Gezondheid />
      ) : vlak === "Bronnen" ? (
        <Bronnen />
      ) : vlak === "Configuratie" ? (
        <Configuratie />
      ) : vlak === "Kwaliteit" ? (
        <Kwaliteit />
      ) : vlak === "Kosten" ? (
        <Kosten />
      ) : vlak === "Meterkast" ? (
        <Meterkast />
      ) : vlak === "Ketenbalans" ? (
        <Ketenbalans />
      ) : vlak === "Validatie" ? (
        <Validatie />
      ) : vlak === "Plannen" ? (
        <Plannen />
      ) : (
        <Gebruikers gebruikers={gebruikers} mijnEmail={mijnEmail} />
      )}
    </div>
  );
}
